import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { projectsService } from '@/features/project/api/projects';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { queryClient } from '@/shared/lib/query-client';
import { createLogger } from '@/shared/lib/logger';

const logger = createLogger('GoalsAutosaveCoordinator');

const AUTO_SAVE_DELAY_MS = 800;
const SAVED_DISPLAY_MS = 2000;

export type GoalsSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface GoalsSaveState {
  status: GoalsSaveStatus;
  error: string | null;
}

const DEFAULT_STATE: GoalsSaveState = { status: 'idle', error: null };

interface GoalsAutosaveStore {
  states: Record<string, GoalsSaveState>;
  setState: (projectId: string, state: GoalsSaveState) => void;
}

const useGoalsAutosaveStore = create<GoalsAutosaveStore>()(
  immer((set) => ({
    states: {},
    setState: (projectId, state) =>
      set((draft) => {
        draft.states[projectId] = state;
      }),
  }))
);

interface Pending {
  goals: string;
  region: string;
  /**
   * Facade area in the project's display unit. `undefined` = untouched (field
   * omitted from the PATCH). Sending it — even unchanged — marks the area
   * user-owned server-side and clears the "estimated" flag, so callers only
   * pass it when the user actually edited the field.
   */
  facadeArea?: number | null;
}

// Per-project mutable plumbing — kept outside the Zustand store because they
// are non-serialisable (timers, in-flight Promises) and we don't want them
// to drive re-renders.
const pending = new Map<string, Pending>();
const debounceTimers = new Map<string, ReturnType<typeof setTimeout>>();
const inFlight = new Map<string, Promise<boolean>>();
const savedResetTimers = new Map<string, ReturnType<typeof setTimeout>>();

function setSaveState(projectId: string, state: GoalsSaveState) {
  useGoalsAutosaveStore.getState().setState(projectId, state);
}

function scheduleSavedReset(projectId: string) {
  const existing = savedResetTimers.get(projectId);
  if (existing) clearTimeout(existing);
  const timer = setTimeout(() => {
    savedResetTimers.delete(projectId);
    const current =
      useGoalsAutosaveStore.getState().states[projectId] ?? DEFAULT_STATE;
    if (current.status === 'saved') {
      setSaveState(projectId, DEFAULT_STATE);
    }
  }, SAVED_DISPLAY_MS);
  savedResetTimers.set(projectId, timer);
}

/**
 * Run the next pending save for `projectId`. Serialises per-project saves so
 * a debounce-fired call and a render-trigger flush can't race the same PATCH.
 * Re-queues `pending` on failure so the user's edits aren't silently lost
 * — the next schedule/flush retries.
 */
async function performSave(projectId: string): Promise<boolean> {
  const existing = inFlight.get(projectId);
  if (existing) {
    // Wait for the in-flight save to finish, then re-check pending — it may
    // have been swept up by that save or there may be newer keystrokes.
    await existing;
  }

  const target = pending.get(projectId);
  if (!target) return true;
  pending.delete(projectId);

  setSaveState(projectId, { status: 'saving', error: null });

  const promise: Promise<boolean> = (async () => {
    try {
      await projectsService.update(projectId, {
        goals: target.goals,
        construction_region: target.region,
        ...(target.facadeArea !== undefined
          ? { facade_area: target.facadeArea }
          : {}),
      });
      return true;
    } catch (err) {
      logger.warn('Goals PATCH failed', { projectId, error: err });
      return false;
    }
  })();

  inFlight.set(projectId, promise);
  let succeeded = false;
  try {
    succeeded = await promise;
  } finally {
    inFlight.delete(projectId);
  }

  if (succeeded) {
    setSaveState(projectId, { status: 'saved', error: null });
    scheduleSavedReset(projectId);
    // Refresh TanStack Query cache so the editor's `useProject` hook reflects
    // the new server-side baseline. Mirrors the `onSuccess` behaviour the old
    // `useMutation` had.
    queryClient.invalidateQueries({
      queryKey: cacheKeys.projects.detail(projectId),
    });
    // The estimate-tab intro (empty state) shows region/units derived from
    // the goals' construction_region — a goals save must refresh it too.
    queryClient.invalidateQueries({
      queryKey: cacheKeys.estimates.introByProject(projectId),
    });
    return true;
  } else {
    pending.set(projectId, target);
    setSaveState(projectId, {
      status: 'error',
      error: 'Save failed',
    });
    return false;
  }
}

export const goalsAutosaveCoordinator = {
  /**
   * Buffer the latest edit and schedule a debounced PATCH. Cancels any
   * previously-scheduled debounce so rapid keystrokes coalesce into one
   * request.
   */
  schedule(
    projectId: string,
    goals: string,
    region: string,
    facadeArea?: number | null
  ) {
    pending.set(projectId, {
      goals: goals.trim(),
      region: region.trim(),
      facadeArea,
    });
    const existing = debounceTimers.get(projectId);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
      debounceTimers.delete(projectId);
      void performSave(projectId);
    }, AUTO_SAVE_DELAY_MS);
    debounceTimers.set(projectId, timer);
  },

  /**
   * Buffer the latest edit and fire the PATCH immediately (no debounce),
   * fire-and-forget. Use from UI handlers like `onBlur` or sheet-close
   * where we want the save to start right away but can't await.
   */
  flushNow(
    projectId: string,
    goals: string,
    region: string,
    facadeArea?: number | null
  ) {
    pending.set(projectId, {
      goals: goals.trim(),
      region: region.trim(),
      facadeArea,
    });
    const existing = debounceTimers.get(projectId);
    if (existing) {
      clearTimeout(existing);
      debounceTimers.delete(projectId);
    }
    void performSave(projectId);
  },

  /**
   * Drop the debounce, run any pending save now, and resolve once the
   * server confirms (or fails). Resolves `false` on save failure so callers
   * (e.g. render trigger) can surface the error and avoid launching a job
   * that would snapshot stale goals server-side.
   *
   * Resolves `true` when there is nothing pending — safe to call
   * unconditionally before every render submit.
   */
  async flushAndAwait(projectId: string): Promise<boolean> {
    const existing = debounceTimers.get(projectId);
    if (existing) {
      clearTimeout(existing);
      debounceTimers.delete(projectId);
    }
    return performSave(projectId);
  },

  clearError(projectId: string) {
    const current =
      useGoalsAutosaveStore.getState().states[projectId] ?? DEFAULT_STATE;
    if (current.status !== 'saving') {
      setSaveState(projectId, DEFAULT_STATE);
    }
  },

  /**
   * Drop everything for a single project: pending payload, debounce timer,
   * "saved" reset timer, and the cached save state. Used by `useDeleteProject`
   * — without this, a debounced PATCH fires after the project row is gone and
   * the server returns 404, surfacing as a phantom "Save failed" toast.
   *
   * Does NOT cancel an already-in-flight Promise (no AbortController in
   * `projectsService.update` yet); on completion the response is still
   * dropped because the project is no longer in the cache.
   */
  cancel(projectId: string) {
    pending.delete(projectId);
    const debounceTimer = debounceTimers.get(projectId);
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimers.delete(projectId);
    }
    const savedTimer = savedResetTimers.get(projectId);
    if (savedTimer) {
      clearTimeout(savedTimer);
      savedResetTimers.delete(projectId);
    }
    setSaveState(projectId, DEFAULT_STATE);
  },

  /**
   * Wipe every per-project timer/queue. Called from `auth.store.logout()` so
   * a debounced PATCH can't fire with a now-revoked token after the user has
   * already left the app. Same class of leak as the W3.1 cross-account
   * localStorage one — different mechanism, same blast radius.
   */
  cancelAll() {
    pending.clear();
    debounceTimers.forEach((timer) => clearTimeout(timer));
    debounceTimers.clear();
    savedResetTimers.forEach((timer) => clearTimeout(timer));
    savedResetTimers.clear();
    // Drop every cached save state so the next account's project ids don't
    // hydrate with a stale "saved"/"error" badge.
    useGoalsAutosaveStore.setState({ states: {} });
  },
};

/**
 * React hook returning the current save state for a project. Re-renders when
 * the coordinator changes status / error for this projectId.
 */
export function useGoalsSaveState(projectId: string): GoalsSaveState {
  return useGoalsAutosaveStore(
    (state) => state.states[projectId] ?? DEFAULT_STATE
  );
}
